import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { addRecent } from "@/hooks/usePrefs";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { RecordPanels } from "@/components/shared/RecordPanels";
import { StatusPill } from "@/components/shared/StatusPill";
import { ContactDialog } from "@/components/crm/ContactDialog";
import { formatPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Building2, Mail, Phone, Pencil, Handshake, CheckSquare, Trash2 } from "lucide-react";

export default function ContactProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has } = useAtlas();
  const [s, setS] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [edit, setEdit] = useState(false);
  const canManage = has("people.manage");

  const load = () => api.get(`/people/${id}/summary`).then((r) => {
    setS(r.data);
    const p = r.data.person;
    addRecent({ type: "person", id, label: `${p.first_name} ${p.last_name}`, link: `/crm/people/${id}` });
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [id]);

  if (!s) return <div className="text-slate-400">Loading…</div>;
  const p = s.person;
  const name = `${p.first_name} ${p.last_name}`;

  const remove = async () => {
    if (!window.confirm(`Delete contact "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/people/${id}`); toast.success("Contact deleted"); navigate("/crm/people"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="contact-profile">
      <button onClick={() => navigate("/crm/people")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="contact-back"><ArrowLeft className="w-4 h-4" /> Contacts</button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="w-14 h-14 rounded-xl bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-xl mb-4">{(p.first_name?.[0] || "") + (p.last_name?.[0] || "")}</div>
              {canManage && <button onClick={() => setEdit(true)} className="text-slate-400 hover:text-slate-800" data-testid="contact-edit" title="Edit details"><Pencil className="w-4 h-4" /></button>}
            </div>
            <h1 className="text-xl font-heading font-semibold text-slate-900">{name}</h1>
            <p className="text-sm text-slate-500">{p.title || "Contact"}{p.person_type ? ` · ${p.person_type.replace(/_/g, " ")}` : ""}</p>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              {p.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><a href={`mailto:${p.email}`} className="text-blue-600 truncate">{p.email}</a></div>}
              {p.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{formatPhone(p.phone)}</div>}
              {s.organization && <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /><button onClick={() => navigate(`/crm/organizations/${s.organization.id}`)} className="text-blue-600 hover:underline truncate text-left" data-testid="contact-org-link">{s.organization.name}</button>{s.organization.lifecycle_stage && <StatusPill value={s.organization.lifecycle_stage} />}</div>}
            </div>
          </div>

          {has("crm.opportunities.view") && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="contact-opportunities">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><Handshake className="w-4 h-4" /> Opportunities</h3>
              {s.opportunities.length === 0 && <p className="text-sm text-slate-400">No related opportunities.</p>}
              {s.opportunities.map((o) => <button key={o.id} onClick={() => navigate("/crm/opportunities")} className="w-full text-sm text-slate-700 py-1 flex justify-between gap-2 row-hover px-1 rounded text-left" data-testid={`contact-opp-${o.id}`}><span className="truncate">{o.name}</span><span className="flex items-center gap-2"><StatusPill value={o.status || "open"} /><span className="font-mono tabular text-slate-500">${(o.amount || 0).toLocaleString()}</span></span></button>)}
            </div>
          )}

          {has("tasks.view") && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="contact-open-tasks">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><CheckSquare className="w-4 h-4" /> Open tasks</h3>
              {s.open_tasks.length === 0 && <p className="text-sm text-slate-400">No open tasks reference this contact.</p>}
              {s.open_tasks.map((t) => <div key={t.id} className="text-sm text-slate-700 py-1 flex items-center gap-2"><CheckSquare className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="flex-1 truncate">{t.title}</span>{t.due_date && <span className="text-xs text-slate-400 tabular">{t.due_date}</span>}</div>)}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <RecordPanels relatedType="person" relatedId={p.id} relatedLabel={name} sourceApp="crm" />
        </div>
      </div>

      <ContactDialog open={edit} onOpenChange={setEdit} contact={p} orgs={orgs} onSaved={load} />
      {canManage && (
        <div className="mt-6 flex justify-end"><button onClick={remove} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1.5" data-testid="contact-delete"><Trash2 className="w-4 h-4" /> Delete contact</button></div>
      )}
    </div>
  );
}
